/**
 * PricingTable — pre-PMF pricing surface.
 *
 * Three tier cards (Free / Pro / Enterprise) up top for the "decide" moment,
 * with a collapsible feature matrix below for buyers doing diligence. Every
 * tier, quota, feature label, and card highlight comes from PLANS — adding
 * a quota or feature in `convex/billing/plans.ts` flows into both surfaces
 * automatically. Enterprise has no listed price; the CTA routes to sales.
 */
import * as React from 'react';
import { Check, X, Sparkle, ArrowRight } from '@phosphor-icons/react';
import { Button, Text } from '@fragments-sdk/ui';
import {
  PLANS,
  FEATURES,
  FEATURE_LABELS,
  FEATURE_DETAILS,
  PLAN_IDS,
  QUOTA_LABELS,
  QUOTA_DETAILS,
  QUOTAS,
  ENTITLEMENT_AREAS,
  getAnnualSavingsPercent,
  getPlanCardCopy,
  formatQuotaLimit,
  type PlanId,
  type Quota,
} from '../../../convex/billing/plans';
import s from './billing.module.scss';

const ENTERPRISE_CONTACT_HREF =
  'mailto:hello@usefragments.com?subject=Enterprise%20inquiry';
const RECOMMENDED_PLAN: PlanId = 'pro';
const HIDDEN_QUOTAS: ReadonlySet<Quota> = new Set<Quota>(['maxRepoBindings']);

type Interval = 'monthly' | 'annual';

interface PricingTableProps {
  currentPlan: PlanId;
  onSelectPlan?: (plan: PlanId, interval: Interval) => void;
  disabledReason?: string;
}

export function PricingTable({
  currentPlan,
  onSelectPlan,
  disabledReason = 'Ask an admin',
}: PricingTableProps) {
  const [interval, setInterval] = React.useState<Interval>('annual');
  const [comparing, setComparing] = React.useState(false);

  const maxSavings = Math.max(
    0,
    ...PLAN_IDS.map(getAnnualSavingsPercent).filter(Boolean),
  );

  return (
    <section className={s.pricing} aria-label="Pricing plans">
      <header className={s.pricingHeader}>
        <IntervalToggle
          interval={interval}
          onChange={setInterval}
          maxSavings={maxSavings}
        />
      </header>

      <div className={s.pricingTiers}>
        {PLAN_IDS.map((planId) => (
          <TierCard
            key={planId}
            planId={planId}
            interval={interval}
            currentPlan={currentPlan}
            onSelectPlan={onSelectPlan}
            disabledReason={disabledReason}
          />
        ))}
      </div>

      <div className={s.compareWrap}>
        <button
          type="button"
          className={s.compareToggle}
          onClick={() => setComparing((v) => !v)}
          aria-expanded={comparing}
        >
          {comparing ? 'Hide full comparison' : 'Compare all features'}
          <ArrowRight
            size={11}
            weight="bold"
            data-rotate={comparing || undefined}
          />
        </button>
        {comparing && <ComparisonMatrix />}
      </div>

      <p className={s.pricingFootnote}>
        Open source? Get started free. Need an MSA, PO, or VPC deploy?{' '}
        <a href={ENTERPRISE_CONTACT_HREF}>Talk to sales</a>.
      </p>
    </section>
  );
}

// ───────────────────────────────────────────────────────────────────────────────
// Interval toggle
// ───────────────────────────────────────────────────────────────────────────────

function IntervalToggle({
  interval,
  onChange,
  maxSavings,
}: {
  interval: Interval;
  onChange: (i: Interval) => void;
  maxSavings: number;
}) {
  return (
    <div
      className={s.intervalToggle}
      role="radiogroup"
      aria-label="Billing interval"
    >
      <button
        type="button"
        role="radio"
        aria-checked={interval === 'monthly'}
        className={s.intervalBtn}
        data-active={interval === 'monthly' || undefined}
        onClick={() => onChange('monthly')}
      >
        Monthly
      </button>
      <button
        type="button"
        role="radio"
        aria-checked={interval === 'annual'}
        className={s.intervalBtn}
        data-active={interval === 'annual' || undefined}
        onClick={() => onChange('annual')}
      >
        Annual
        {maxSavings > 0 && (
          <span className={s.intervalSave}>save {maxSavings}%</span>
        )}
      </button>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────────
// Tier card
// ───────────────────────────────────────────────────────────────────────────────

interface TierCardProps {
  planId: PlanId;
  interval: Interval;
  currentPlan: PlanId;
  onSelectPlan?: (plan: PlanId, interval: Interval) => void;
  disabledReason: string;
}

function TierCard({
  planId,
  interval,
  currentPlan,
  onSelectPlan,
  disabledReason,
}: TierCardProps) {
  const plan = PLANS[planId];
  const pricing = plan.pricing;
  const copy = getPlanCardCopy(planId);
  const isCurrent = planId === currentPlan;
  const isRecommended = planId === RECOMMENDED_PLAN;
  const isFree = !pricing && planId === 'free';
  const isContactSales = !pricing && !isFree;

  return (
    <article
      className={s.tier}
      data-current={isCurrent || undefined}
      data-recommended={isRecommended || undefined}
      data-tier={planId}
    >
      {isRecommended && (
        <span className={s.tierRibbon}>
          <Sparkle size={10} weight="fill" />
          Recommended
        </span>
      )}

      <div className={s.tierHead}>
        <Text as="h3" size="md" weight="semibold" className={s.tierName}>
          {plan.label}
        </Text>
        <span className={s.tierTagline}>{plan.tagline}</span>
      </div>

      <TierPrice planId={planId} interval={interval} />

      <div className={s.tierCta}>
        {renderCta({
          isCurrent,
          isContactSales,
          isFree,
          isRecommended,
          plan,
          planId,
          interval,
          onSelectPlan,
          disabledReason,
        })}
      </div>

      <div className={s.tierFeatures}>
        {copy.upgradeFrom && (
          <span className={s.tierFeaturesLead}>{copy.upgradeFrom}</span>
        )}
        <ul className={s.tierFeatureList}>
          {copy.bullets.map((bullet) => (
            <li key={bullet} className={s.tierFeature}>
              <Check size={13} weight="bold" aria-hidden />
              <span>{bullet}</span>
            </li>
          ))}
        </ul>
      </div>
    </article>
  );
}

function TierPrice({
  planId,
  interval,
}: {
  planId: PlanId;
  interval: Interval;
}) {
  const plan = PLANS[planId];
  const pricing = plan.pricing;

  if (pricing) {
    const amount = interval === 'annual' ? pricing.annual : pricing.monthly;
    return (
      <div className={s.tierPrice}>
        <div className={s.tierPriceMain}>
          <span className={s.tierCurrency}>$</span>
          <span className={s.tierAmount}>{amount}</span>
          <span className={s.tierUnit}>/seat/mo</span>
        </div>
        <span className={s.tierPriceMeta}>
          {interval === 'annual'
            ? `Billed annually · $${pricing.annual * 12}/seat/yr`
            : 'Billed monthly · cancel anytime'}
        </span>
      </div>
    );
  }

  if (planId === 'free') {
    return (
      <div className={s.tierPrice}>
        <div className={s.tierPriceMain}>
          <span className={s.tierCurrency}>$</span>
          <span className={s.tierAmount}>0</span>
          <span className={s.tierUnit}>forever</span>
        </div>
        <span className={s.tierPriceMeta}>No credit card required</span>
      </div>
    );
  }

  return (
    <div className={s.tierPrice}>
      <div className={s.tierPriceMain}>
        <span className={s.tierAmount} data-text>
          Custom
        </span>
      </div>
      <span className={s.tierPriceMeta}>Annual contracts · volume pricing</span>
    </div>
  );
}

function renderCta(args: {
  isCurrent: boolean;
  isContactSales: boolean;
  isFree: boolean;
  isRecommended: boolean;
  plan: (typeof PLANS)[PlanId];
  planId: PlanId;
  interval: Interval;
  onSelectPlan?: (plan: PlanId, interval: Interval) => void;
  disabledReason: string;
}) {
  const {
    isCurrent,
    isContactSales,
    isFree,
    isRecommended,
    plan,
    planId,
    interval,
    onSelectPlan,
    disabledReason,
  } = args;

  if (isCurrent) {
    return <span className={s.tierStatus}>Current plan</span>;
  }
  if (isContactSales) {
    return (
      <Button variant="outlined" size="sm" fullWidth asChild>
        <a href={ENTERPRISE_CONTACT_HREF}>
          Talk to sales
          <ArrowRight size={12} weight="bold" />
        </a>
      </Button>
    );
  }
  if (isFree) {
    return <span className={s.tierStatus}>Included on signup</span>;
  }
  if (!onSelectPlan) {
    return <span className={s.tierStatus}>{disabledReason}</span>;
  }
  return (
    <Button
      type="button"
      variant={isRecommended ? 'primary' : 'outlined'}
      size="sm"
      fullWidth
      onClick={() => onSelectPlan(planId, interval)}
    >
      {plan.trial ? `Start ${plan.trial.days}-day trial` : 'Upgrade'}
    </Button>
  );
}

// ───────────────────────────────────────────────────────────────────────────────
// Comparison matrix (collapsed by default)
// ───────────────────────────────────────────────────────────────────────────────

function ComparisonMatrix() {
  return (
    <div className={s.matrix} role="table" aria-label="Plan comparison">
      <div className={s.matrixHeadRow} role="row">
        <span role="columnheader" className={s.matrixHeadLabel} />
        {PLAN_IDS.map((planId) => (
          <span
            key={planId}
            role="columnheader"
            className={s.matrixHeadCell}
            data-recommended={planId === RECOMMENDED_PLAN || undefined}
          >
            {PLANS[planId].label}
          </span>
        ))}
      </div>

      {ENTITLEMENT_AREAS.map((area) => {
        const quotas = QUOTAS.filter(
          (q) => !HIDDEN_QUOTAS.has(q) && QUOTA_DETAILS[q].area === area.id,
        );
        const features = FEATURES.filter(
          (f) => FEATURE_DETAILS[f].area === area.id,
        );
        if (quotas.length + features.length === 0) return null;

        return (
          <React.Fragment key={area.id}>
            <div className={s.matrixGroup} role="row">
              <span role="rowheader" className={s.matrixGroupLabel}>
                {area.label}
              </span>
            </div>
            {quotas.map((q) => (
              <div key={q} className={s.matrixRow} role="row">
                <span role="rowheader" className={s.matrixRowLabel}>
                  {QUOTA_LABELS[q]}
                </span>
                {PLAN_IDS.map((planId) => (
                  <span key={planId} role="cell" className={s.matrixCell}>
                    {formatQuotaLimit(PLANS[planId].quotas[q])}
                  </span>
                ))}
              </div>
            ))}
            {features.map((f) => (
              <div key={f} className={s.matrixRow} role="row">
                <span role="rowheader" className={s.matrixRowLabel}>
                  {FEATURE_LABELS[f]}
                </span>
                {PLAN_IDS.map((planId) => (
                  <span key={planId} role="cell" className={s.matrixCell}>
                    {PLANS[planId].features[f] ? (
                      <Check
                        size={13}
                        weight="bold"
                        className={s.matrixCheck}
                        aria-label="Included"
                      />
                    ) : (
                      <X
                        size={13}
                        className={s.matrixDash}
                        aria-label="Not included"
                      />
                    )}
                  </span>
                ))}
              </div>
            ))}
          </React.Fragment>
        );
      })}
    </div>
  );
}
